import * as React from "react";
import { SlidersHorizontal } from "lucide-react";
import { cn } from "../../lib/utils";

/**
 * Reusable filter dropdown. The popover stays open while the user toggles
 * options (unlike a Radix menu), so it suits multi-select filter panels.
 *
 * children can be a node, or a render function receiving { close }.
 */
function FilterPopover({
  label = "Filters",
  icon: Icon = SlidersHorizontal,
  count = 0,
  align = "left",
  buttonClassName,
  panelClassName,
  children,
}) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "inline-flex h-9 items-center gap-2 rounded-lg border border-input bg-background px-3 text-sm font-medium text-foreground transition-colors hover:bg-muted",
          count > 0 && "border-primary/40 text-primary",
          buttonClassName,
        )}
      >
        {Icon && <Icon className="size-4" />}
        {label}
        {count > 0 && (
          <span className="ml-0.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-xs font-medium text-primary-foreground">
            {count}
          </span>
        )}
      </button>
      {open && (
        <div
          className={cn(
            "absolute z-30 mt-1 min-w-56 rounded-xl border border-border bg-popover p-3 text-popover-foreground shadow-lg",
            align === "right" ? "right-0" : "left-0",
            panelClassName,
          )}
        >
          {typeof children === "function"
            ? children({ close: () => setOpen(false) })
            : children}
        </div>
      )}
    </div>
  );
}

export { FilterPopover };
